import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  RefreshControl,
  Platform,
  FlatList,
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useTranslation } from 'react-i18next';
import { RootStackParamList, Club } from '../types';
import { AppHeader, ScreenLayout, SearchInput, ClubCard, SectionHeader, EmptyState, PageBand, Button } from '../components';
import { Colors, Fonts, Spacing, Typography, LAYOUT } from '../constants';
import { clubService } from '../services/club';
import { useResponsive } from '../utils/responsive';
import { useDialog, useUser, useUserClubs } from '../contexts';
import { useDebounce } from '../hooks';

type Props = NativeStackScreenProps<RootStackParamList, 'Clubs'>;

export const ClubsScreen: React.FC<Props> = ({ navigation }) => {
  const { t } = useTranslation();
  const dialog = useDialog();
  const { user } = useUser();
  const { userClubs, refreshClubs } = useUserClubs();
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [joiningId, setJoiningId] = useState<string | null>(null);
  const { isDesktop } = useResponsive();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const ITEMS_PER_PAGE = 20;
  const columns = isDesktop ? 4 : 1;

  const joinedIds = useMemo(() => new Set(userClubs.map(uc => uc.club_id)), [userClubs]);

  const loadClubs = async (reset: boolean = false) => {
    try {
      const currentOffset = reset ? 0 : offset;
      const searchQuery = debouncedSearch.trim();

      let result;
      if (searchQuery) {
        result = await clubService.searchClubs(searchQuery, {
          offset: currentOffset,
          limit: ITEMS_PER_PAGE,
        });
      } else {
        result = await clubService.getClubsPaginated({
          offset: currentOffset,
          limit: ITEMS_PER_PAGE,
        });
      }

      if (reset) {
        setClubs(result.data);
        setOffset(ITEMS_PER_PAGE);
      } else {
        setClubs((prev) => [...prev, ...result.data]);
        setOffset((prev) => prev + ITEMS_PER_PAGE);
      }
      setHasMore(result.hasMore);
    } catch (error: any) {
      dialog.alert(t('common.errors.title'), t('screens.clubs.loadError'), 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadClubs();
  }, []);

  // Reset and reload when search term changes
  useEffect(() => {
    if (!loading) {
      setLoading(true);
      setOffset(0);
      loadClubs(true);
    }
  }, [debouncedSearch]);

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    setOffset(0);
    setHasMore(true);
    loadClubs(true);
    refreshClubs();
  }, [debouncedSearch, refreshClubs]);

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      loadClubs(false);
    }
  };

  const openClub = (club: Club) => navigation.navigate('ClubDetail', { clubId: club.id });

  const handleJoin = async (club: Club) => {
    if (!user) {
      navigation.navigate('Login');
      return;
    }
    try {
      setJoiningId(club.id);
      await clubService.joinClub(club.id);
      await refreshClubs();
    } catch (error: any) {
      dialog.alert(t('common.errors.title'), error.message || t('screens.clubDetail.joinFailed'), 'error');
    } finally {
      setJoiningId(null);
    }
  };

  // "My clubs" comes from the membership context (already loaded), filtered by
  // the same search term; the paginated list below shows everything else.
  const myClubs = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return userClubs
      .map(uc => uc.club)
      .filter((c): c is Club => !!c)
      .filter(c => !q || c.name.toLowerCase().includes(q))
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  }, [userClubs, searchTerm]);

  const otherClubs = useMemo(() => clubs.filter(c => !joinedIds.has(c.id)), [clubs, joinedIds]);

  const renderClub = ({ item }: { item: Club }) => (
    <View style={isDesktop ? styles.gridCell : undefined}>
      <ClubCard
        mobile={!isDesktop}
        club={item}
        joined={joinedIds.has(item.id)}
        onPress={() => openClub(item)}
        onJoin={() => handleJoin(item)}
        joinLoading={joiningId === item.id}
        style={isDesktop ? undefined : styles.rowCard}
      />
    </View>
  );

  const renderEmpty = () => (
    <View style={styles.centerContainer}>
      <Text style={[isDesktop ? Typography.section : Typography.sectionMobile, styles.emptyText]}>
        {searchTerm ? t('screens.clubs.noSearchResults') : t('screens.clubs.noResults')}
      </Text>
      <Text style={styles.emptySubtext}>
        {searchTerm ? t('screens.clubs.noSearchResultsSubtext') : t('screens.clubs.noResultsSubtext')}
      </Text>
    </View>
  );

  const listHeader = (
    <View>
      {user ? (
        <View style={styles.section}>
          <SectionHeader
            mobile={!isDesktop}
            title={t('screens.clubs.myClubs')}
            kicker={t('screens.clubs.count', { count: myClubs.length })}
            action={isDesktop ? undefined : t('screens.clubs.ranking')}
            onAction={() => navigation.navigate('ClubLeaderboard')}
          />
          {myClubs.length === 0 ? (
            isDesktop ? (
              <Text style={styles.noClubsLine}>{t('screens.clubs.noClubsJoined')}</Text>
            ) : (
              <EmptyState
                icon="people-outline"
                title={t('screens.clubs.noClubsJoined')}
                subtitle={t('screens.clubs.noClubsJoinedSubtext')}
              />
            )
          ) : (
            <View style={isDesktop ? styles.grid : undefined}>
              {myClubs.map(club => (
                <View key={club.id} style={isDesktop ? styles.gridCell : undefined}>
                  <ClubCard
                    mobile={!isDesktop}
                    club={club}
                    joined
                    onPress={() => openClub(club)}
                    style={isDesktop ? undefined : styles.rowCard}
                  />
                </View>
              ))}
            </View>
          )}
        </View>
      ) : null}
      <SectionHeader
        mobile={!isDesktop}
        title={user ? t('screens.clubs.otherClubs') : t('screens.clubs.title')}
        kicker={t('screens.clubs.count', { count: otherClubs.length })}
        style={styles.otherHeader}
      />
    </View>
  );

  // Desktop: the headline lives in the green band and the search straddles its
  // lower edge. Mobile keeps the white AppHeader with the search underneath.
  const band = (
    <PageBand
      title={t('screens.clubs.title')}
      kicker={t('screens.clubs.count', { count: myClubs.length + otherClubs.length })}
      right={
        <Button
          title={t('screens.clubLeaderboard.title')}
          icon="trophy-outline"
          variant="white"
          size="sm"
          onPress={() => navigation.navigate('ClubLeaderboard')}
        />
      }
      toolbar={
        <SearchInput
          bordered
          value={searchTerm}
          onChangeText={setSearchTerm}
          placeholder={t('screens.clubs.searchPlaceholder')}
          testID="clubs-search"
        />
      }
    />
  );

  return (
    <ScreenLayout mobileTab="Clubs" desktopPadding band={band}>
      <View style={styles.container}>
        {!isDesktop && (
          <AppHeader
            navigation={navigation}
            currentScreen="Clubs"
            title={t('screens.clubs.title')}
          />
        )}

        {/* Search stays outside the list so the input never loses focus on re-render */}
        {!isDesktop && (
          <View style={styles.searchWrap}>
            <SearchInput
              bordered
              value={searchTerm}
              onChangeText={setSearchTerm}
              placeholder={t('screens.clubs.searchPlaceholder')}
              testID="clubs-search"
            />
          </View>
        )}

        {loading && clubs.length === 0 ? (
          <View style={styles.centerContainer}>
            <Text>{t('common.loading')}</Text>
          </View>
        ) : (
          <FlatList
            key={`clubs-${columns}`}
            data={otherClubs}
            keyExtractor={(item) => item.id}
            renderItem={renderClub}
            numColumns={columns}
            columnWrapperStyle={columns > 1 ? styles.gridRow : undefined}
            ListHeaderComponent={listHeader}
            ListEmptyComponent={renderEmpty}
            contentContainerStyle={isDesktop ? styles.listContentDesktop : styles.listContentMobile}
            refreshControl={
              <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
            }
            onEndReached={handleLoadMore}
            onEndReachedThreshold={0.5}
            ListFooterComponent={
              loading && clubs.length > 0 ? (
                <Text style={styles.loadingMore}>{t('common.loadingMore')}</Text>
              ) : null
            }
          />
        )}
      </View>
    </ScreenLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
  },
  searchWrap: {
    paddingHorizontal: Spacing.base,
    paddingTop: 14,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: Spacing.xl,
  },
  emptyText: {
    color: Colors.textDark,
    marginBottom: 6,
    textAlign: 'center',
  },
  emptySubtext: {
    fontFamily: Fonts.regular,
    fontSize: 14,
    color: Colors.textLight,
    textAlign: 'center',
  },
  noClubsLine: {
    fontFamily: Fonts.regular,
    fontSize: 14,
    lineHeight: 21,
    color: Colors.textLight,
  },
  loadingMore: {
    textAlign: 'center',
    padding: Spacing.base,
    color: Colors.textMedium,
    fontSize: 14,
  },
  listContentMobile: {
    paddingHorizontal: Spacing.base,
    paddingTop: Spacing.xl,
    paddingBottom: LAYOUT.tabBar.contentPadding,
  },
  listContentDesktop: {
    paddingBottom: Spacing.xl,
  },
  section: {
    marginBottom: 28,
  },
  otherHeader: {
    marginBottom: Spacing.base,
  },
  rowCard: {
    marginBottom: 10,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.lg,
  },
  gridRow: {
    gap: Spacing.lg,
    marginBottom: Spacing.lg,
  },
  gridCell: {
    flex: 1,
    minWidth: 0,
    ...(Platform.OS === 'web' && { flexBasis: 'calc(25% - 15px)', flexGrow: 0 } as any),
  },
});
